// Client-side manager for the limited-time 10% urgency offer.
//
// The offer window is authoritatively minted and enforced by the server
// (GET /api/promo/urgency-token + server/promo.ts). We cache the signed token and its
// server-authored expiry in localStorage so the visible countdown and the actually-applied
// discount stay in sync, survive refreshes and return visits, and never silently reset.
// The window is ONE-TIME per browser: once a token has been issued we never mint another,
// so after it expires the offer is genuinely over (no resetting timer). When the visitor
// claims, the server returns a UNIQUE, single-use SAVE10-XXXXXX code that only validates
// while this token is still open, so a copied code cannot be reused or shared.

const STORAGE_KEY = "vro_urgency_offer_v1";
const TEN_CODE_KEY = "vro_ten_coupon"; // the unique 10% code this browser already claimed

export interface UrgencyOffer {
  token: string;
  expiresAt: number; // epoch ms, authored by the server
}

function readStored(): UrgencyOffer | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UrgencyOffer;
    if (!parsed?.token || !parsed?.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(offer: UrgencyOffer): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(offer));
  } catch {
    /* localStorage unavailable — offer will be non-persistent this session */
  }
}

// Returns the active offer only while its window is still open, else null.
export function getUrgencyOffer(): UrgencyOffer | null {
  const stored = readStored();
  if (!stored) return null;
  if (Date.now() >= stored.expiresAt) return null;
  return stored;
}

export function isUrgencyActive(): boolean {
  return getUrgencyOffer() !== null;
}

// The signed token to attach to quote/booking requests, or null if expired/absent.
export function getPromoToken(): string | null {
  return getUrgencyOffer()?.token ?? null;
}

export function getUrgencyExpiry(): number | null {
  return getUrgencyOffer()?.expiresAt ?? null;
}

let inflight: Promise<UrgencyOffer | null> | null = null;

// Ensures a window exists, minting one from the server ONLY on the very first use for this
// browser. If a window was already issued (even if now expired) we never mint another — the
// offer is one-time, so it cannot be farmed by revisiting. Returns the offer while open,
// null once it has closed.
export async function ensureUrgencyOffer(): Promise<UrgencyOffer | null> {
  const existing = readStored();
  if (existing) {
    return Date.now() < existing.expiresAt ? existing : null;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetch("/api/promo/urgency-token", { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.token || !data?.expiresAt) return null;
      const offer: UrgencyOffer = { token: data.token, expiresAt: Number(data.expiresAt) };
      writeStored(offer);
      return offer;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

// Claims this visitor's unique 10% code. Reuses the code already claimed by this browser
// (so we don't mint duplicates) and only mints while the window is open. Returns null if
// the offer has expired.
export async function claimTenPercent(): Promise<{ code: string } | null> {
  const offer = getUrgencyOffer();
  if (!offer) return null;

  try {
    const saved = localStorage.getItem(TEN_CODE_KEY);
    if (saved) return { code: saved };
  } catch {
    /* ignore */
  }

  try {
    const res = await fetch("/api/promo/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token: offer.token }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.code) return null;
    try {
      localStorage.setItem(TEN_CODE_KEY, data.code);
    } catch {
      /* ignore */
    }
    return { code: data.code };
  } catch {
    return null;
  }
}

// HH:MM:SS from a remaining-seconds count (clamped at zero).
/**
 * Countdown for the offer window.
 *
 * Days are split out once there is more than one. The window used to be 24 hours,
 * where HH:MM:SS always read sensibly; at seven days the same format renders
 * "167:59:58", which looks like a bug rather than a deadline.
 */
export function formatCountdown(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(clamped / 86400);
  const hours = Math.floor((clamped % 86400) / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;
  const hms =
    `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return days > 0 ? `${days}d ${hms}` : hms;
}
